import { useState } from "react";
import { toast } from "sonner";
import { LifeBuoy, Send } from "lucide-react";
import api, { formatErr } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { BRAND_COLORS } from "../lib/bookingStatus";

export default function CustomerSupport() {
  const { lang } = useI18n();
  const de = lang === "de";
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async () => {
    const msg = message.trim();
    if (!msg || sending) return;
    setSending(true);
    try {
      await api.post("/support/contact", { message: msg });
      setSent(true);
      setMessage("");
      toast.success(de ? "Ihre Nachricht ist bei uns angekommen." : "Your message has reached us.");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="max-w-[720px] mx-auto px-1 py-2 page-fade" data-testid="customer-support-page">
      <div className="label-caps mb-3" style={{ color: BRAND_COLORS.gold }}>Support</div>
      <h1 className="font-display text-4xl sm:text-5xl text-navy leading-[1.05] mb-3">
        {de ? "Wie können wir helfen?" : "How can we help?"}
      </h1>
      <p className="text-[15px] text-navy-dark/70 mb-8 max-w-xl">
        {de
          ? "Schreiben Sie uns kurz Ihr Anliegen – Ihr Weissblom-Team meldet sich zeitnah bei Ihnen."
          : "Send us a short message – your Weissblom team will get back to you shortly."}
      </p>

      <div className="bg-white border border-navy/10 p-6 md:p-8">
        <label htmlFor="support-message" className="label-caps block mb-2 flex items-center gap-2">
          <LifeBuoy className="w-4 h-4" style={{ color: BRAND_COLORS.gold }} strokeWidth={1.6} />
          {de ? "Ihre Nachricht" : "Your message"}
        </label>
        <textarea
          id="support-message"
          data-testid="support-message-input"
          rows={6}
          value={message}
          onChange={(e) => { setMessage(e.target.value); setSent(false); }}
          placeholder={de ? "Wie können wir Ihnen helfen?" : "How can we help you?"}
          className="w-full bg-white border border-navy/20 focus:border-gold focus:outline-none px-4 py-3 text-navy-dark leading-relaxed resize-y"
        />

        {sent && (
          <p className="mt-4 text-[13px] text-navy" data-testid="support-sent-confirm">
            {de
              ? "Vielen Dank! Ihre Nachricht ist bei uns angekommen – wir melden uns zeitnah."
              : "Thank you! Your message has reached us – we'll be in touch shortly."}
          </p>
        )}

        <div className="mt-6 flex justify-end">
          <button
            type="button"
            data-testid="support-send-btn"
            onClick={submit}
            disabled={!message.trim() || sending}
            className="inline-flex items-center gap-3 text-ivory px-8 py-3.5 text-[12px] tracking-[0.2em] uppercase hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ background: BRAND_COLORS.navy }}
          >
            <Send className="w-4 h-4" style={{ color: BRAND_COLORS.gold }} strokeWidth={1.6} />
            {sending ? (de ? "Wird gesendet …" : "Sending …") : (de ? "Senden" : "Send")}
          </button>
        </div>
      </div>
    </div>
  );
}
